/**
 * Barra de edicion masiva de la tabla de Productos (ADM-03).
 *
 * Aparece cuando hay filas seleccionadas y ofrece las dos cosas que el sitio
 * administra sobre varios productos a la vez: categoria y visibilidad. Precio
 * y stock quedan afuera a proposito: un precio se cambia mirando el producto, y
 * el stock solo cambia con un movimiento de inventario.
 *
 * Las dos operaciones piden confirmacion en un modal antes de aplicarse: a
 * diferencia de destacar un producto (un interruptor, reversible con otro
 * clic), esto puede tocar decenas de productos de una sola vez.
 *
 * La lista de categorias (`obtenerCategoriasAdmin`, ~200 filas) solo se pide
 * cuando se abre el selector, no con la barra: mientras nadie toca "Cambiar
 * categoria" no hace falta traerla.
 */
import { useState } from 'react';

import type { CategoriaAdminResumen } from '@bandu/compartido';

import { usePeticion } from '../../hooks/use-peticion';
import { actualizarProductosMasivo } from '../../servicios/administracion.servicio';
import { obtenerCategoriasAdmin } from '../../servicios/portada.servicio';
import { numero } from '../../utilidades/formato';
import { mensajeDe } from '../../utilidades/mensaje-error';
import { Icono } from '../comunes/Icono';
import { Cargando } from '../comunes/EstadoPeticion';
import { Modal } from './Modal';

export interface BarraEdicionMasivaProps {
  seleccionados: number[];
  onLimpiar(): void;
  /** Se dispara despues de aplicar con exito, para refrescar la tabla. */
  onAplicado(): void;
}

type Accion = 'categoria' | 'publicar' | 'ocultar' | null;

function plural(cantidad: number, singular: string, plural: string): string {
  return cantidad === 1 ? singular : plural;
}

export function BarraEdicionMasiva({ seleccionados, onLimpiar, onAplicado }: BarraEdicionMasivaProps) {
  const [accion, setAccion] = useState<Accion>(null);
  const [categoriaElegida, setCategoriaElegida] = useState<CategoriaAdminResumen | null>(null);
  const [busqueda, setBusqueda] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [falla, setFalla] = useState<string | null>(null);

  // Solo pide la lista mientras el selector de categoria esta abierto. El
  // generico explicito evita que `Promise.resolve([])` infiera `never[]` y
  // rompa la union con `Promise<CategoriaAdminResumen[]>`.
  const categorias = usePeticion<CategoriaAdminResumen[]>(
    (senal) => (accion === 'categoria' ? obtenerCategoriasAdmin(senal) : Promise.resolve([])),
    [accion === 'categoria'],
  );

  if (seleccionados.length === 0) return null;

  const cantidad = seleccionados.length;

  function cerrar(): void {
    if (enviando) return;
    setAccion(null);
    setCategoriaElegida(null);
    setBusqueda('');
    setFalla(null);
  }

  async function aplicar(cambios: { categoriaId?: number; visible?: boolean }): Promise<void> {
    setEnviando(true);
    setFalla(null);
    try {
      await actualizarProductosMasivo({ ids: seleccionados, ...cambios });
      cerrar();
      onAplicado();
    } catch (fallo) {
      setFalla(mensajeDe(fallo));
    } finally {
      setEnviando(false);
    }
  }

  const termino = busqueda.trim().toLowerCase();
  const coincidencias =
    termino.length > 0
      ? (categorias.datos ?? []).filter((categoria) => categoria.nombre.toLowerCase().includes(termino))
      : (categorias.datos ?? []);

  const tituloModal =
    accion === 'categoria' ? 'Cambiar categoria' : accion === 'publicar' ? 'Publicar productos' : 'Ocultar productos';

  return (
    <>
      <div className="barra-edicion-masiva" role="region" aria-label="Edicion masiva de productos">
        <span className="n">
          {numero(cantidad)} {plural(cantidad, 'producto seleccionado', 'productos seleccionados')}
        </span>
        <span className="acciones-fila">
          <button
            type="button"
            className="btn btn-sm btn-contorno"
            onClick={() => {
              setAccion('categoria');
            }}
          >
            <Icono nombre="etiqueta" />
            Cambiar categoria
          </button>
          <button
            type="button"
            className="btn btn-sm btn-contorno"
            onClick={() => {
              setAccion('publicar');
            }}
          >
            <Icono nombre="ojo" />
            Publicar
          </button>
          <button
            type="button"
            className="btn btn-sm btn-contorno"
            onClick={() => {
              setAccion('ocultar');
            }}
          >
            <Icono nombre="ojoNo" />
            Ocultar
          </button>
          <button type="button" className="btn btn-sm btn-suave" onClick={onLimpiar}>
            Cancelar seleccion
          </button>
        </span>
      </div>

      <Modal
        titulo={tituloModal}
        bajada={`${numero(cantidad)} ${plural(cantidad, 'producto', 'productos')}`}
        abierto={accion !== null}
        onCerrar={cerrar}
      >
        {falla !== null ? (
          <p className="acceso-error" role="alert">
            <Icono nombre="alerta" />
            <span>{falla}</span>
          </p>
        ) : null}

        {accion === 'publicar' || accion === 'ocultar' ? (
          <div className="formulario-admin">
            <p>
              {accion === 'publicar'
                ? `Vas a publicar ${numero(cantidad)} ${plural(cantidad, 'producto', 'productos')}. Va a aparecer donde el catalogo lo muestre.`
                : `Vas a ocultar ${numero(cantidad)} ${plural(cantidad, 'producto', 'productos')}. Deja de verse en la tienda hasta que lo vuelvas a publicar.`}
            </p>
            <div className="modal-acciones">
              <button type="button" className="btn btn-contorno" onClick={cerrar} disabled={enviando}>
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-primario"
                disabled={enviando}
                onClick={() => {
                  void aplicar({ visible: accion === 'publicar' });
                }}
              >
                {enviando ? 'Aplicando…' : accion === 'publicar' ? 'Publicar' : 'Ocultar'}
              </button>
            </div>
          </div>
        ) : null}

        {accion === 'categoria' && categoriaElegida === null ? (
          <div className="formulario-admin">
            <div className="campo">
              <label htmlFor="buscar-categoria-masiva">Buscar categoria</label>
              <input
                id="buscar-categoria-masiva"
                type="search"
                autoComplete="off"
                placeholder="Escribe el nombre"
                value={busqueda}
                onChange={(evento) => {
                  setBusqueda(evento.target.value);
                }}
              />
            </div>

            {categorias.estado === 'cargando' ? <Cargando que="las categorias" /> : null}

            <ul className="lista-marcados">
              {coincidencias.map((categoria) => (
                <li key={categoria.id}>
                  <span className="marcado-nombre">
                    <span className="n">{categoria.nombre}</span>
                    <span className="s">{categoria.lineaNombre}</span>
                  </span>
                  <button
                    type="button"
                    className="btn btn-xs btn-primario"
                    onClick={() => {
                      setCategoriaElegida(categoria);
                    }}
                  >
                    Elegir
                  </button>
                </li>
              ))}
              {categorias.estado === 'listo' && coincidencias.length === 0 ? (
                <li>
                  <span className="celda-sub">Ninguna categoria coincide.</span>
                </li>
              ) : null}
            </ul>
          </div>
        ) : null}

        {accion === 'categoria' && categoriaElegida !== null ? (
          <div className="formulario-admin">
            <p>
              Vas a mover {numero(cantidad)} {plural(cantidad, 'producto', 'productos')} a{' '}
              <strong>{categoriaElegida.nombre}</strong>.
            </p>
            <div className="modal-acciones">
              <button
                type="button"
                className="btn btn-contorno"
                disabled={enviando}
                onClick={() => {
                  setCategoriaElegida(null);
                }}
              >
                Elegir otra
              </button>
              <button
                type="button"
                className="btn btn-primario"
                disabled={enviando}
                onClick={() => {
                  void aplicar({ categoriaId: categoriaElegida.id });
                }}
              >
                {enviando ? 'Aplicando…' : 'Confirmar'}
              </button>
            </div>
          </div>
        ) : null}
      </Modal>
    </>
  );
}
